<template>
    <canvas id="hkcm_home_graph_canvas"></canvas>
</template>

<style>
#hkcm_home_graph_canvas {
    position: sticky;
    top: 0;
    z-index: -1;
    width: 100% !important;
    height: 100vh !important;
}
#hkcm_home_graph_canvas:after {
    content: ' ';
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background: rgb(255, 255, 255);
    background: linear-gradient(45deg, rgba(255, 255, 255, 1) 10%, rgba(255, 255, 255, 0) 30%);
    background: red;
}
</style>

<script type="module">
import { isProxy, toRaw } from 'vue';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { asset } from '../../helpers.js';
//import Stats from 'stats.js'
import { plot, NoiseHelper } from './noise.js';

export default {
    props: ['cam-distance'],
    data() {
        return {
            //config
            currentFps: 35,
            fpsLimitFocus: 35,
            fpsLimitBackground1: 24,
            fpsLimitBackground2: 18,
            inViewport: true,
            camDistance: 6,
            graphSpeed: 0.3, //actual graph move speed from left to right (m/s)
            graphPeriod: Math.PI * 4, //value length of one complete graph period
            graphProgressSpeed: 0.05, //speed of the graph progression in relation to the movement speed
            graphDir: -1, //1 = to right, -1 = to left
            graphSize: new THREE.Vector2(5, 1.5),
            planeBGSize: new THREE.Vector2(14, 4),
            planeTickSize: new THREE.Vector3(0.5, 0.3, 0.01),
            planeTickTextSize: new THREE.Vector2(0.22, 0.22),
            graphOffsetX: 0,
            tickSpawnOffset: 2,
            fillGraphOnStart: true,
            graphShowTimeout: 2000,
            instancedCandles: true,
            instancedCandlesCount: 70, //~60 are used + a buffer of 10

            candleBaseHeight: 0.2,
            candleBaseRadius: 0.01,
            candleTipHeight: 0.3,
            candleTipRadius: 0.002,
            candleSpacing: 0.02,
            candleFadeTime: 0.1,
            candleColor: 0x031837,

            tickFadeTime: 0.3,
            tickHightlightTime: 0.3,
            tickTextTime: 2,
            tickTextHeight: 0.5,

            //scroll animation steps
            planePos: [new THREE.Vector3(2, 0.5, 3), new THREE.Vector3(-1, 0, 2), new THREE.Vector3(-1, 0, 2), new THREE.Vector3(0, -0.2, 4)],
            planeRot: [new THREE.Vector3(-0.7, 0, Math.PI / 5), new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0)],
            blur: [0, 0, 0, 10],
            opacity: [1, 1, 1, 0.2],
            tickTilt: [Math.PI * 0.25, 0, 0, 0],

            //tick spawn positions
            tickSpawns: [
                { offset: 0.05, type: 'zones', align: 'dip', text: 'high' },
                { offset: 0.07, type: 'zones', align: 'peak', text: 'low' },
                { offset: 0.115, type: 'zones', align: 'dip', text: 'low' },
                { offset: 0.136, type: 'area2', align: 'peak', text: 'low' },
                { offset: 0.23, type: 'zones', align: 'dip', text: 'high' },
                { offset: 0.28, type: 'zones', align: 'peak', text: 'high' },
                { offset: 0.365, type: 'area2', align: 'dip', text: 'high' },
                { offset: 0.39, type: 'zones', align: 'peak', text: 'low' },
                { offset: 0.47, type: 'area2', align: 'dip', text: 'low' },
                { offset: 0.57, type: 'zones', align: 'peak', text: 'high' },
                { offset: 0.62, type: 'zones', align: 'dip', text: 'high' },
                { offset: 0.68, type: 'zones', align: 'dip', text: 'high' },
                { offset: 0.77, type: 'zones', align: 'peak', text: 'high' },
                { offset: 0.865, type: 'zones', align: 'dip', text: 'high' },
                { offset: 0.89, type: 'zones', align: 'peak', text: 'low' },
                { offset: 0.93, type: 'area1', align: 'dip', text: 'low' }
            ],

            //helper constants
            _fov: 85,
            _cubeSize: 1,
            _refDistance: 1,
            _refCanvasHeight: 800,
            _dummyTransform: null,
            _outOfBounds: null,

            //dynamic data vars
            _graphTime: 0.0,
            _currentTickIndex: 0,
            _deltaSum: 0,

            _stops: [],
            _ticks: [],
            _candles: [],

            mounted: false,

            //obj pools
            _objPools: {
                free: {},
                used: {},
                indices: {}
            },

            //textures
            _tex_graph_bg: null,

            _tex_graph_tick_bg_refs: {
                zones: {
                    predicted: [],
                    passed: []
                },
                area1: {
                    predicted: [],
                    passed: []
                },
                area2: {
                    predicted: [],
                    passed: []
                }
            },

            _tex_graph_tick_text_refs: {
                low: [],
                high: []
            }
        };
    },
    mounted() {
        window.addEventListener('resize', this._onResize, false);
        window.addEventListener('scroll', this.checkViewport);

        this._tex_graph_bg = asset('/hkcm/images/pgraph/graph_bg3.png', 'public');

        this._tex_graph_tick_bgs = {
            zones: {
                predicted: [
                    asset('/hkcm/images/pgraph/graph_tick2_predicted.png', 'public'),
                    asset('/hkcm/images/pgraph/graph_tick3_predicted.png', 'public'),
                    asset('/hkcm/images/pgraph/graph_tick5_predicted.png', 'public')
                ],
                passed: [
                    asset('/hkcm/images/pgraph/graph_tick2_passed.png', 'public'),
                    asset('/hkcm/images/pgraph/graph_tick3_passed.png', 'public'),
                    asset('/hkcm/images/pgraph/graph_tick5_passed.png', 'public')
                ]
            },
            area1: {
                predicted: [asset('/hkcm/images/pgraph/graph_tick1_predicted.png', 'public')],
                passed: [asset('/hkcm/images/pgraph/graph_tick1_passed.png', 'public')]
            },
            area2: {
                predicted: [asset('/hkcm/images/pgraph/graph_tick4_predicted.png', 'public')],
                passed: [asset('/hkcm/images/pgraph/graph_tick4_passed.png', 'public')]
            }
        };

        this._tex_graph_tick_texts = {
            low: [
                asset('/hkcm/images/pgraph/graph_tick_text3_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text4_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text9_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text10_alt.png', 'public')
            ],
            high: [
                asset('/hkcm/images/pgraph/graph_tick_text1_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text2_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text5_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text6_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text7_alt.png', 'public'),
                asset('/hkcm/images/pgraph/graph_tick_text8_alt.png', 'public')
            ]
        };

        this.mounted = true;
        this.initCanvas();
        //this.handleFrame();
    },
    unmounted() {
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('scroll', this.checkViewport);

        this.mounted = false;

        if (this.renderID) cancelAnimationFrame(this.renderID);
    },
    methods: {
        initCanvas() {
            this.canvas = document.getElementById('hkcm_home_graph_canvas');

            this.regenerateStops();

            this.clock = new THREE.Clock();
            this.stats = null; //new Stats()
            if (this.stats) {
                this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
                document.body.appendChild(this.stats.dom);
            }

            this._fpsLimitInv = 1 / this.currentFps;

            this._dummyTransform = new THREE.Object3D();
            this._dummyTransform.position.set(-500, 0, 0);
            this._dummyTransform.updateMatrix();
            this._outOfBounds = this._dummyTransform.matrix;

            this.noiseHelper = new NoiseHelper(0);

            this.lookTarget = new THREE.Vector3();

            this.scene = new THREE.Scene();
            //this.scene.background = new THREE.Color('transparent');

            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                antialias: true,
                alpha: true,
                powerPreference: 'high-performance'
            });
            this.renderer.shadowMap.enabled = true;
            this.renderer.shadowMap.renderSingleSided = false;
            this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
            this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);
            this.renderer.setPixelRatio(window.devicePixelRatio);
            //this.renderer.setClearColor(0x000000, 0);

            this.ambientLight = new THREE.AmbientLight(0xcfcfcf); // soft white light
            this.scene.add(this.ambientLight);

            this.directionalLight = new THREE.DirectionalLight(0xffffff, 1);
            this.directionalLight.castShadow = true;
            this.directionalLight.shadow.mapSize.width = 512;
            this.directionalLight.shadow.mapSize.height = 512;
            const d = 5;
            this.directionalLight.shadow.camera.left = -d;
            this.directionalLight.shadow.camera.right = d;
            this.directionalLight.shadow.camera.top = d;
            this.directionalLight.shadow.camera.bottom = -d;
            this.directionalLight.shadow.camera.near = 0.1;
            this.directionalLight.shadow.camera.far = 50;
            this.directionalLight.position.set(0, 0, 10);
            this.scene.add(this.directionalLight);

            const aspect = this.canvas.clientWidth / this.canvas.clientHeight;
            const near = 0.1;
            const far = 10000;
            this.camera = new THREE.PerspectiveCamera(this._fov, aspect, near, far);

            this.camera.position.set(0, 0, this.camDistance);

            const textureLoader = new THREE.TextureLoader();
            textureLoader.crossOrigin = '';

            const maxAnisotropy = this.renderer.capabilities.getMaxAnisotropy();

            const graphTextureBG = textureLoader.load(this._tex_graph_bg);
            graphTextureBG.anisotropy = maxAnisotropy;

            for (var groupKey in this._tex_graph_tick_bgs) {
                for (var key in this._tex_graph_tick_bgs[groupKey]['predicted']) {
                    var tex = textureLoader.load(this._tex_graph_tick_bgs[groupKey]['predicted'][key]);
                    tex.anisotropy = maxAnisotropy;
                    this._tex_graph_tick_bg_refs[groupKey]['predicted'].push(tex);
                }

                for (var key in this._tex_graph_tick_bgs[groupKey]['passed']) {
                    var tex = textureLoader.load(this._tex_graph_tick_bgs[groupKey]['passed'][key]);
                    tex.anisotropy = maxAnisotropy;
                    this._tex_graph_tick_bg_refs[groupKey]['passed'].push(tex);
                }
            }

            for (var groupKey in this._tex_graph_tick_texts) {
                for (var key in this._tex_graph_tick_texts[groupKey]) {
                    var tex = textureLoader.load(this._tex_graph_tick_texts[groupKey][key]);
                    tex.anisotropy = maxAnisotropy;
                    this._tex_graph_tick_text_refs[groupKey].push(tex);
                }
            }

            const widget = this;
            this._graphBounds = this.graphSize.x * 0.5; // - (this.planeTickSize.x * 0.5);
            this._candleBaseScaler = 1 / this.candleBaseHeight;
            this._candleTipScaler = 1 / (this.candleBaseHeight + this.candleTipHeight);
            this._tickSpawnGraphOffset = (this.tickSpawnOffset / this.graphSpeed) * this.graphProgressSpeed * this.graphSpeed;
            this._candleOffset = this.candleBaseRadius * 2 + this.candleSpacing;
            this._tickOffset = 0;

            this._geomCandleBase = new THREE.CylinderGeometry(this.candleBaseRadius, this.candleBaseRadius, this.candleBaseHeight, 6, 1);
            this._geomCandleTip = new THREE.CylinderGeometry(this.candleTipRadius, this.candleTipRadius, this.candleBaseHeight + this.candleTipHeight, 3, 1);
            this._matCandleBase = new THREE.MeshBasicMaterial({ color: this.candleColor });
            this._matCandleTip = new THREE.MeshBasicMaterial({ color: this.candleColor });

            this._planeRoot = new THREE.Group();
            this._candleRoot = new THREE.Group();

            if (this.instancedCandles) {
                this._instancedCandleBase = new THREE.InstancedMesh(this._geomCandleBase, this._matCandleBase, this.instancedCandlesCount);
                this._instancedCandleBase.castShadow = true;
                this._planeRoot.add(this._instancedCandleBase);
                this._instancedCandleTip = new THREE.InstancedMesh(this._geomCandleTip, this._matCandleTip, this.instancedCandlesCount);
                this._instancedCandleTip.castShadow = true;
                this._planeRoot.add(this._instancedCandleTip);
            }

            this._candleRoot.position.x = this.graphOffsetX;

            this._planeRoot.add(this._candleRoot);

            // Grid material with custom shader
            const customFragmentShader = `

                #define PHONG
                uniform vec3 diffuse;
                uniform vec3 emissive;
                uniform vec3 specular;
                uniform float shininess;
                uniform float opacity;
                #include <common>
                #include <packing>
                #include <dithering_pars_fragment>
                #include <color_pars_fragment>
                #include <uv_pars_fragment>
                #include <uv2_pars_fragment>
                #include <map_pars_fragment>
                #include <alphamap_pars_fragment>
                #include <alphatest_pars_fragment>
                #include <aomap_pars_fragment>
                #include <lightmap_pars_fragment>
                #include <emissivemap_pars_fragment>
                #include <envmap_common_pars_fragment>
                #include <envmap_pars_fragment>
                #include <fog_pars_fragment>
                #include <bsdfs>
                #include <lights_pars_begin>
                #include <normal_pars_fragment>
                #include <lights_phong_pars_fragment>
                #include <shadowmap_pars_fragment>
                #include <bumpmap_pars_fragment>
                #include <normalmap_pars_fragment>
                #include <specularmap_pars_fragment>
                #include <logdepthbuf_pars_fragment>
                #include <clipping_planes_pars_fragment>

                    uniform sampler2D map;
                    varying vec2 vUv;

                    // Noise function to calculate alpha
                    float noise(float x, float y) {
                        // Replace this with your noise function call
                        return 0.2; // Example, replace with your implementation
                    }

                void main() {
                    #include <clipping_planes_fragment>
                    vec4 diffuseColor = vec4( diffuse, opacity );
                    ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
                    vec3 totalEmissiveRadiance = emissive;
                    #include <logdepthbuf_fragment>
                    #include <map_fragment>

                        vec4 texColor = texture2D(map, vUv);

                        // Calculate alpha using the noise function
                        float alpha = noise(vUv.x, vUv.y);

                        // Set the final fragment color with the alpha
                        gl_FragColor = vec4(texColor.rgb, alpha);

                    #include <color_fragment>
                    #include <alphamap_fragment>
                    #include <alphatest_fragment>
                    #include <specularmap_fragment>
                    #include <normal_fragment_begin>
                    #include <normal_fragment_maps>
                    #include <emissivemap_fragment>
                    #include <lights_phong_fragment>
                    #include <lights_fragment_begin>
                    #include <lights_fragment_maps>
                    #include <lights_fragment_end>
                    #include <aomap_fragment>
                    vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;
                    #include <envmap_fragment>
                    #include <output_fragment>
                    #include <tonemapping_fragment>
                    #include <encodings_fragment>
                    #include <fog_fragment>
                    #include <premultiplied_alpha_fragment>
                    #include <dithering_fragment>
                }

                    `;
            // Create the custom material
            const planeMaterialBG2 = new THREE.MeshBasicMaterial({ map: graphTextureBG /**/, transparent: true, alphaTest: 0.1 /**/ });
            // Use onBeforeCompile to set the custom fragment shader
            planeMaterialBG2.onBeforeCompile = (shader) => {
                shader.uniforms.graphTime = { value: 0.0001 };
                shader.uniforms.noiseScale = { value: 5.0 };
                shader.uniforms.noiseSpeed = { value: 0.2 };
                shader.fragmentShader = shader.fragmentShader.replace(
                    '#include <dithering_fragment>',

                    `// Calculate alpha using the noise function
                            //float alpha = (simplex3d(vec3(vUv.x * noiseScale + graphTime * 255.0 * noiseSpeed, vUv.y * noiseScale + graphTime * 255.0 * noiseSpeed, 0.0)) + 1.0) * 0.5;
                            float alpha = simplex3d(vec3(vUv.x * noiseScale + graphTime * 255.0 * noiseSpeed, vUv.y * noiseScale + graphTime * 255.0 * noiseSpeed, 0.0));

                            // Set the final fragment color with the alpha
                            vec3 newColor = vec3(0.0,0.0,1.0);
                            newColor = mix(gl_FragColor.rgb, newColor, alpha);// * alpha);

                            gl_FragColor = vec4(newColor, gl_FragColor.a * alpha);

                            #include <dithering_fragment>`
                );

                shader.fragmentShader = shader.fragmentShader.replace(
                    '#include <common>',

                    `
float hue2rgb(float f1, float f2, float hue) {
    if (hue < 0.0)
        hue += 1.0;
    else if (hue > 1.0)
        hue -= 1.0;
    float res;
    if ((6.0 * hue) < 1.0)
        res = f1 + (f2 - f1) * 6.0 * hue;
    else if ((2.0 * hue) < 1.0)
        res = f2;
    else if ((3.0 * hue) < 2.0)
        res = f1 + (f2 - f1) * ((2.0 / 3.0) - hue) * 6.0;
    else
        res = f1;
    return res;
}

vec3 hsl2rgb(vec3 hsl) {
    vec3 rgb;

    if (hsl.y == 0.0) {
        rgb = vec3(hsl.z); // Luminance
    } else {
        float f2;

        if (hsl.z < 0.5)
            f2 = hsl.z * (1.0 + hsl.y);
        else
            f2 = hsl.z + hsl.y - hsl.y * hsl.z;

        float f1 = 2.0 * hsl.z - f2;

        rgb.r = hue2rgb(f1, f2, hsl.x + (1.0/3.0));
        rgb.g = hue2rgb(f1, f2, hsl.x);
        rgb.b = hue2rgb(f1, f2, hsl.x - (1.0/3.0));
    }
    return rgb;
}

vec3 hsl2rgb(float h, float s, float l) {
    return hsl2rgb(vec3(h, s, l));
}

vec3 random3(vec3 c) {
	float j = 4096.0*sin(dot(c,vec3(17.0, 59.4, 15.0)));
	vec3 r;
	r.z = fract(512.0*j);
	j *= .125;
	r.x = fract(512.0*j);
	j *= .125;
	r.y = fract(512.0*j);
	return r-0.5;
}

const float F3 =  0.3333333;
const float G3 =  0.1666667;

float simplex3d(vec3 p) {
	 vec3 s = floor(p + dot(p, vec3(F3)));
	 vec3 x = p - s + dot(s, vec3(G3));

	 vec3 e = step(vec3(0.0), x - x.yzx);
	 vec3 i1 = e*(1.0 - e.zxy);
	 vec3 i2 = 1.0 - e.zxy*(1.0 - e);

	 vec3 x1 = x - i1 + G3;
	 vec3 x2 = x - i2 + 2.0*G3;
	 vec3 x3 = x - 1.0 + 3.0*G3;

	 vec4 w, d;

	 w.x = dot(x, x);
	 w.y = dot(x1, x1);
	 w.z = dot(x2, x2);
	 w.w = dot(x3, x3);

	 w = max(0.6 - w, 0.0);

	 d.x = dot(random3(s), x);
	 d.y = dot(random3(s + i1), x1);
	 d.z = dot(random3(s + i2), x2);
	 d.w = dot(random3(s + 1.0), x3);

	 w *= w;
	 w *= w;
	 d *= w;

	 return dot(d, vec4(52.0));
}

float hash(vec2 p) { return fract(1e4 * sin(17.0 * p.x + p.y * 0.1) * (0.1 + abs(sin(p.y * 13.0 + p.x)))); }

                            uniform float noiseScale;
                            uniform float noiseSpeed;
                            uniform float graphTime;

                            #include <common>`
                );

                planeMaterialBG2.userData.shader = shader;
            };
            planeMaterialBG2.shininess = 0;

            // Grid mesh
            const gridGeometry = new THREE.PlaneGeometry(this.planeBGSize.x, this.planeBGSize.y, 20, 20);
            const gridMesh = new THREE.Mesh(gridGeometry, planeMaterialBG2);
            gridMesh.receiveShadow = true;
            //gridMesh.rotation.x = -Math.PI / 2;
            gridMesh.position.z = -2;
            gridMesh.position.y = 0;
            this._planeRoot.add(gridMesh);
            this._graphBGMat = planeMaterialBG2;

            const planeGeometryBG = new THREE.PlaneGeometry(this.planeBGSize.x, this.planeBGSize.y);
            const planeMaterialBG = new THREE.MeshBasicMaterial({ map: graphTextureBG, transparent: true, alphaTest: 0.1 });
            planeMaterialBG.shininess = 0;
            const planeBG = new THREE.Mesh(planeGeometryBG, planeMaterialBG);
            planeBG.castShadow = false;
            planeBG.position.x = -0.8;
            planeBG.position.z = -2;
            planeBG.position.y = 0;
            //this._planeRoot.add(planeBG);

            const planeGeometryBGShadow = new THREE.PlaneGeometry(this.planeBGSize.x + 4, this.planeBGSize.y + 4);
            const planeMaterialBGShadow = new THREE.ShadowMaterial({
                color: 0xf5f5f5,
                transparent: true
            }); //new THREE.MeshPhongMaterial ({ color: new THREE.Color('white'), transparent: true });
            const planeBGS = new THREE.Mesh(planeGeometryBGShadow, planeMaterialBGShadow);
            planeBGS.castShadow = false;
            planeBGS.receiveShadow = true;
            planeBGS.position.x = -0.8;
            planeBGS.position.z = -2.1;
            this._planeRoot.add(planeBGS);

            this.scene.add(this._planeRoot);

            if (this.fillGraphOnStart) {
                //spawn missing candles
                for (var x = -this.graphSize.x / 2; x <= this._candleOffset; x += this._candleOffset) {
                    this.addCandle(
                        this.evaluateBaseGraphTime(x * this.graphProgressSpeed) * this.graphSize.y,
                        x,
                        THREE.MathUtils.clamp(this.candleBaseHeight * Math.random(), 0.05, this.candleBaseHeight),
                        THREE.MathUtils.clamp(this.candleTipHeight * Math.random(), 0.05, this.candleBaseHeight)
                    );
                }

                //spawn missing ticks
                for (var i = 0; i < this.tickSpawns.length; i++) {
                    const tick = this.tickSpawns[i];
                    const tickStartOffset = tick.offset - this._tickSpawnGraphOffset; // % 1);

                    const localOffset = -(this._graphTime - tickStartOffset + 1) / this.graphProgressSpeed;
                    const xPos = this.tickSpawnOffset + localOffset;

                    var state = 'idle';
                    if (this.graphDir > 0 ? xPos > this._graphBounds : xPos < -this._graphBounds) state = 'despawn';

                    this.addTick(tick.offset - 1, tick.type, tick.align, tick.text, this.evaluateBaseGraphTime(tick.offset) * this.graphSize.y, xPos, state);
                }
            }

            this._debugDiv = document.getElementById('hkcm_graph_debug');
            this._debugCanvasGraph = document.getElementById('hkcm_graph_debug_graph');

            //first render size fix
            setTimeout(function () {
                widget._onResize();
                widget.handleFrame();
            }, this.graphShowTimeout);
            /*setInterval( function() {
                    widget.renderID = requestAnimationFrame(widget.handleFrame);
                }, 1000 / this.fpsLimit );*/
        },

        handleFrame() {
            if (!this.mounted) return;

            var widget = this;
            this.renderID = requestAnimationFrame(this.handleFrame);

            /*setTimeout( function() {
                    widget.renderID = requestAnimationFrame(widget.handleFrame);
                }, this._fpsLimitInv * 1000);*/

            var delta = this.clock.getDelta();

            if (!(delta > 0) || delta > 1) {
                //skip first frame (without delta) and any spike frame from paused execution (tab change, delta > 1)
                this.renderer.render(this.scene, this.camera);
                //this.renderID = requestAnimationFrame(this.handleFrame);
                return;
            }

            //calc frame times
            this._deltaSum += delta;
            this._deltaRenderSum += delta;
            const isRenderFrame = this._deltaSum >= this._fpsLimitInv;

            //begin stats measure
            if (this.stats) this.stats.begin();

            //execute handlers
            if (isRenderFrame) {
                const framesCount = Math.floor(this._deltaSum / this._fpsLimitInv) || 1;

                this.update(this._fpsLimitInv * framesCount); //this._deltaSum);
                this.render(this._fpsLimitInv * framesCount); //this._deltaSum);

                this._deltaSumLast = this._deltaSum;
                this._deltaSum = this._deltaSum % this._fpsLimitInv;
            }

            //debug
            if (this._debugDiv) {
                !document.hidden;
                this._debugDiv.innerHTML =
                    'Graph Progress: ' +
                    this._graphTime +
                    '<br/>' +
                    'Pools: ' +
                    Object.keys(this._objPools.free.candles || []).length +
                    '|' +
                    Object.keys(this._objPools.used.candles || []).length +
                    ' (candles)<br/>' +
                    'Ticks: ' +
                    Object.keys(this._ticks).length +
                    '<br/>' +
                    'Candles: ' +
                    Object.keys(this._candles).length +
                    '<br/>' +
                    'Delta: ' +
                    delta +
                    '<br/>' +
                    'FPS: ' +
                    1 / delta +
                    '<br/>' +
                    'FPS (Fixed): ' +
                    1 / this._deltaSumLast;
            }
            if (this._debugCanvasGraph) {
                plot(this._debugCanvasGraph, (x) => this.evaluateBaseGraph(x), this.graphPeriod / 2, this._graphTime, this.tickSpawns);
            }

            if (this.stats) this.stats.end();

            //this.renderID = requestAnimationFrame(this.handleFrame);
        },

        update(delta) {
            //progress graph
            this._graphTime += Math.abs(this.graphDir) * this.graphSpeed * this.graphProgressSpeed * delta;

            if (this._graphTime > 1) {
                this._graphTime -= 1;
                this._tickOffset = 0;
                for (var key in this._ticks) {
                    this._ticks[key].offsetTarget -= 1;
                }
            }
            this._graphBGMat.userData.shader.uniforms.graphTime.value = this._graphTime;

            //get new positions
            const scrollPos = this.getScrollPos();
            const baseGraphPos = this.evaluateBaseGraphTime(this._graphTime) * this.graphSize.y;

            const newPos = this.getLerpedPos(this.planePos, scrollPos.index, scrollPos.progress);
            const newRot = this.getLerpedPos(this.planeRot, scrollPos.index, scrollPos.progress);
            const newBlur = this.getLerpedFloat(this.blur, scrollPos.index, scrollPos.progress);
            const newOpacity = this.getLerpedFloat(this.opacity, scrollPos.index, scrollPos.progress);
            const newTilt = this.getLerpedFloat(this.tickTilt, scrollPos.index, scrollPos.progress);

            //apply general plane animation
            this._planeRoot.position.x = newPos.x;
            this._planeRoot.position.y = newPos.y;
            this._planeRoot.position.z = newPos.z;

            this._planeRoot.rotation.x = newRot.x;
            this._planeRoot.rotation.y = newRot.y;
            this._planeRoot.rotation.z = newRot.z;

            //this.camera.lookAt(newTarget);

            //apply general blur animation
            this.canvas.style.filter = 'blur(' + newBlur + 'px)';
            this.canvas.style.opacity = newOpacity;

            //move candles
            var candleIndex = 0;
            for (var key in this._candles) {
                const candle = this._candles[key];

                candle.update(this, delta, candleIndex);

                if (candle.despawned) this.removeCandle(candle, key);

                candleIndex++;
            }

            //spawn new candles
            var spawnCandleCount = 0;
            while (
                (!this._lastCandle || (this.graphDir > 0 ? this._lastCandle.root.position.x > this._candleOffset : this._lastCandle.root.position.x < -this._candleOffset)) &&
                spawnCandleCount < 20
            ) {
                spawnCandleCount++;
                var offsetX = this.graphDir > 0 ? this._lastCandle.root.position.x - this._candleOffset : this._lastCandle.root.position.x + this._candleOffset;
                var candle = this.addCandle(
                    this.evaluateBaseGraphTime(this._graphTime + offsetX * this.graphProgressSpeed) * this.graphSize.y,
                    offsetX,
                    THREE.MathUtils.clamp(this.candleBaseHeight * Math.random(), 0.05, this.candleBaseHeight),
                    THREE.MathUtils.clamp(this.candleTipHeight * Math.random(), 0.05, this.candleBaseHeight)
                );

                if (candle) {
                    candle.update(this, 0, candleIndex);
                    candleIndex++;
                } else {
                    break;
                }
            }

            //hide unused instances out-of-bounds
            if (this.instancedCandles) {
                for (var i = candleIndex; i < this.instancedCandlesCount; i++) {
                    this._instancedCandleBase.setMatrixAt(i, this._outOfBounds);
                    this._instancedCandleTip.setMatrixAt(i, this._outOfBounds);
                }
            }

            //move ticks
            for (var key in this._ticks) {
                const tick = this._ticks[key];

                tick.update(this, delta);

                //despawn tick
                if (tick.despawned) this.removeTick(tick, key);
            }

            //spawn new ticks
            const nextTick = this.tickSpawns[this._currentTickIndex];
            var tickStartOffset = nextTick.offset - (this._tickSpawnGraphOffset % 1) + (this._tickOffset || 0);

            if (tickStartOffset < this._graphTime) {
                const localOffset = -(this._graphTime - tickStartOffset) / this.graphProgressSpeed;
                this.addTick(
                    nextTick.offset + this._tickOffset,
                    nextTick.type,
                    nextTick.align,
                    nextTick.text,
                    this.evaluateBaseGraphTime(nextTick.offset) * this.graphSize.y,
                    this.tickSpawnOffset + localOffset,
                    'spawning'
                );

                this._currentTickIndex++;

                if (this._currentTickIndex >= this.tickSpawns.length) {
                    this._currentTickIndex = 0;
                    this._tickOffset = 1;
                }
            }

            //update instanced meshes
            if (this.instancedCandles) {
                this._instancedCandleBase.instanceMatrix.needsUpdate = true;
                this._instancedCandleTip.instanceMatrix.needsUpdate = true;
            }
        },

        render(delta) {
            this.renderer.render(this.scene, this.camera);
        },

        addTick(offsetTarget, type, align, textTexIndex, yOffset, xOffset, initState) {
            var bgIndex = Math.floor(Math.random() * this._tex_graph_tick_bg_refs[type]['predicted'].length);
            var widget = this;
            var tick = this.getFreePoolObject('ticks|' + type + '|' + bgIndex + '|' + textTexIndex, function () {
                //console.log("add tick ("+bgIndex+")");
                //const tickTop = (isTop ? 1 : -1) * widget.graphSize.y * 0.5;
                //const tickRight = widget.graphSize.x * 0.5;

                //const bgTex = Math.random() > 0.5 ? widget._tex_graph_tick_light_ref : widget._tex_graph_tick_dark_ref;

                const tickRoot = new THREE.Group();
                tickRoot.position.z = 0.001;

                //const planeGeometryTickBG =  new THREE.PlaneGeometry(widget.planeTickSize.x, widget.planeTickSize.y);
                const planeGeometryTickBG = new THREE.BoxGeometry(widget.planeTickSize.x, widget.planeTickSize.y, widget.planeTickSize.z);
                const planeMaterialTickBG = new THREE.MeshBasicMaterial({ map: widget._tex_graph_tick_bg_refs[type]['predicted'][bgIndex], transparent: true, alphaTest: 0.1 });
                planeMaterialTickBG.shininess = 0;
                //planeMaterialTickBG.shadowSide = THREE.DoubleSide;
                const tickBG = new THREE.Mesh(planeGeometryTickBG, planeMaterialTickBG);
                tickBG.castShadow = true;
                tickBG.receiveShadow = false;
                tickRoot.add(tickBG);

                const tickTextPivot = new THREE.Group();
                tickTextPivot.position.y = widget.planeTickSize.y * 0.5;
                const planeGeometryTickText = new THREE.PlaneGeometry(widget.planeTickTextSize.x, widget.planeTickTextSize.y);
                const planeText = widget._tex_graph_tick_text_refs[textTexIndex][Math.floor(Math.random() * widget._tex_graph_tick_text_refs[textTexIndex].length)];
                const planeMaterialTickText = new THREE.MeshBasicMaterial({ map: planeText, transparent: true, alphaTest: 0.1 });
                planeMaterialTickText.shininess = 0;
                //planeMaterialTickText.shadowSide = THREE.DoubleSide;
                const tickText = new THREE.Mesh(planeGeometryTickText, planeMaterialTickText);
                tickText.visible = false;
                tickText.castShadow = true;
                tickText.receiveShadow = false;
                tickText.position.y = widget.planeTickSize.y * 0.5 + widget.planeTickTextSize.y * 0.5;
                tickText.position.z = widget.planeTickSize.z;
                tickTextPivot.add(tickText);
                tickRoot.add(tickTextPivot);

                tickRoot.renderOrder = 1;
                widget._planeRoot.add(tickRoot);

                return {
                    root: tickRoot,
                    pivot: tickTextPivot,
                    bg: tickBG,
                    text: tickText,
                    offsetTarget: offsetTarget,
                    tickType: type,
                    bgIndex: bgIndex,
                    textTexIndex: textTexIndex,

                    state: 'spawning',
                    stateProgress: 0,
                    highlighted: false,
                    despawned: false,
                    setPassed: function (widget, highlighted) {
                        if (highlighted) {
                            this.bg.material.map = widget._tex_graph_tick_bg_refs[this.tickType]['passed'][this.bgIndex];
                            this.highlighted = true;
                            this.text.visible = true;
                            this.pivot.position.y = 0;
                            this.text.material.opacity = 0;
                        } else {
                            this.bg.material.map = widget._tex_graph_tick_bg_refs[this.tickType]['predicted'][this.bgIndex];
                            this.highlighted = false;
                            this.text.visible = false;
                            this.pivot.position.y = 0;
                            this.text.material.opacity = 0;
                        }
                    },
                    reset: function () {
                        this.root.position.x = 0;
                        this.root.scale.y = 1;
                        this.stateProgress = 0;
                        this.state = 'spawning';
                        this.despawned = false;
                        this.bg.visible = true;
                        this.text.visible = false;
                    },
                    update: function (widget, delta) {
                        this.root.position.x += widget.graphDir * widget.graphSpeed * delta;

                        switch (this.state) {
                            case 'spawning':
                                if (this.stateProgress < widget.tickFadeTime) {
                                    this.stateProgress += delta;
                                    this.root.scale.x = this.stateProgress / widget.tickFadeTime;
                                } else {
                                    this.root.scale.x = 1;
                                    this.stateProgress = 0;
                                    this.state = 'pending';
                                }
                                break;

                            case 'pending':
                                if (this.offsetTarget < widget._graphTime) {
                                    this.state = 'highlight';
                                    this.setPassed(widget, true);
                                }
                                break;

                            case 'highlight':
                                this.stateProgress += delta;

                                if (this.stateProgress < widget.tickHightlightTime) {
                                    const scaler = 1 + Math.sin((this.stateProgress / widget.tickHightlightTime) * Math.PI) * 0.1;
                                    this.bg.scale.x = scaler;
                                    this.bg.scale.y = scaler;
                                } else {
                                    this.bg.scale.x = 1;
                                    this.bg.scale.y = 1;
                                }

                                if (this.stateProgress < widget.tickTextTime) {
                                    this.pivot.position.y = (this.stateProgress / widget.tickTextTime) * widget.tickTextHeight;
                                    const x = (1 - this.stateProgress / widget.tickTextTime) * (Math.PI / 2);
                                    this.text.material.opacity = Math.sin(x * x); //1 - (this.stateProgress / widget.tickTextTime);
                                } else {
                                    this.pivot.position.y = widget.tickTextHeight;
                                    this.text.material.opacity = 0;
                                    this.text.visible = false;
                                }

                                if (this.stateProgress > widget.tickHightlightTime && this.stateProgress > widget.tickTextTime) {
                                    this.stateProgress = 0;
                                    this.state = 'idle';
                                }
                                break;

                            case 'idle':
                                if (widget.graphDir > 0 ? this.root.position.x > widget._graphBounds : this.root.position.x < -widget._graphBounds) this.state = 'despawn';
                                break;

                            case 'despawn':
                                if (this.stateProgress < widget.tickFadeTime) {
                                    this.stateProgress += delta;
                                    this.root.scale.x = 1 - this.stateProgress / widget.tickFadeTime;
                                } else {
                                    this.root.scale.x = 1;
                                    this.stateProgress = 0;
                                    this.despawned = true;
                                    this.bg.visible = false;
                                    this.text.visible = false;
                                }
                                break;

                            case 'none':
                                break;
                        }
                    }
                };
            });

            if (tick) {
                tick.reset();
                tick.state = initState;

                tick.offsetTarget = offsetTarget;
                switch (tick.state) {
                    case 'highlight':
                    case 'idle':
                    case 'despawn':
                        tick.setPassed(widget, true);
                        break;

                    default:
                        tick.setPassed(widget, false);
                        break;
                }

                var yTypeOffset = 0;
                switch (align) {
                    case 'peak':
                        yTypeOffset = this.planeTickSize.y * 0.25;
                        break;

                    case 'dip':
                        yTypeOffset = -this.planeTickSize.y * 0.25;
                        break;
                }

                tick.root.position.x = xOffset;
                tick.root.position.y = yOffset + yTypeOffset;

                this._ticks.push(tick);
            }

            return tick;
        },

        removeTick(tick, tickKey) {
            /*tick.pivot.remove(tick.text);
                tick.root.remove(tick.bg);
                tick.root.remove(tick.pivot);
                this.scene.remove(tick.root);*/

            this.returnPoolObject('ticks|' + tick.tickType + '|' + tick.bgIndex + '|' + tick.textTexIndex, tick);
            delete this._ticks[tickKey];
        },

        addCandle(yOffset, xOffset, baseLength, tipLength) {
            const widget = this;
            const scaleBase = baseLength * this._candleBaseScaler;
            const scaleTip = (baseLength + tipLength) * this._candleTipScaler;
            var candle = this.getFreePoolObject('candles', function () {
                //console.log("add candle");
                var candleRoot;
                var instancedCandleBase;
                var instancedCandleTip;
                if (widget.instancedCandles) {
                    candleRoot = new THREE.Object3D();
                    instancedCandleBase = new THREE.Object3D();
                    instancedCandleTip = new THREE.Object3D();
                } else {
                    candleRoot = new THREE.Group();
                    instancedCandleBase = new THREE.Mesh(widget._geomCandleBase, widget._matCandleBase);
                    instancedCandleTip = new THREE.Mesh(widget._geomCandleTip, widget._matCandleTip);

                    instancedCandleBase.castShadow = true;
                    instancedCandleTip.castShadow = true;

                    candleRoot.add(instancedCandleBase);
                    candleRoot.add(instancedCandleTip);

                    widget._candleRoot.add(candleRoot);
                }

                return {
                    root: candleRoot,
                    base: instancedCandleBase,
                    tip: instancedCandleTip,

                    state: 'spawning',
                    stateProgress: 0,
                    despawned: false,

                    scaleBase: scaleBase,
                    scaleTip: scaleTip,

                    reset: function () {
                        this.root.position.x = 0;
                        this.root.scale.y = 0;
                        this.stateProgress = 0;
                        this.state = 'spawning';
                        this.despawned = false;
                        this.base.visible = true;
                        this.tip.visible = true;
                    },
                    update: function (widget, delta, candleIndex) {
                        this.root.position.x += widget.graphDir * widget.graphSpeed * delta;

                        if (widget.instancedCandles) {
                            this.base.position.set(this.root.position.x, this.root.position.y, this.root.position.z);
                            this.base.scale.y = this.root.scale.y * this.scaleBase;
                            this.base.updateMatrix();
                            widget._instancedCandleBase.setMatrixAt(candleIndex, this.base.matrix);

                            this.tip.position.set(this.root.position.x, this.root.position.y, this.root.position.z);
                            this.tip.scale.y = this.root.scale.y * this.scaleTip;
                            this.tip.updateMatrix();
                            widget._instancedCandleTip.setMatrixAt(candleIndex, this.tip.matrix);
                        }

                        switch (this.state) {
                            case 'spawning':
                                if (this.stateProgress < widget.candleFadeTime) {
                                    this.stateProgress += delta;
                                    this.root.scale.y = this.stateProgress / widget.candleFadeTime;
                                } else {
                                    this.root.scale.y = 1;
                                    this.stateProgress = 0;
                                    this.state = 'idle';
                                }
                                break;

                            case 'idle':
                                if (widget.graphDir > 0 ? this.root.position.x > widget._graphBounds : this.root.position.x < -widget._graphBounds) this.state = 'despawn';
                                break;

                            case 'despawn':
                                if (this.stateProgress < widget.candleFadeTime) {
                                    this.stateProgress += delta;
                                    this.root.scale.y = 1 - this.stateProgress / widget.candleFadeTime;
                                } else {
                                    this.root.scale.y = 1;
                                    this.stateProgress = 0;
                                    this.base.visible = false;
                                    this.tip.visible = false;
                                    this.despawned = true;
                                }
                                break;

                            case 'none':
                                break;
                        }
                    }
                };
            });

            if (candle) {
                candle.reset();

                candle.root.position.x = xOffset;
                candle.root.position.y = yOffset;

                candle.scaleBase = baseLength * this._candleBaseScaler;
                candle.scaleTip = (baseLength + tipLength) * this._candleTipScaler;

                candle.base.scale.y = candle.scaleBase;
                candle.tip.scale.y = candle.scaleTip;

                this._candles.push(candle);
                this._lastCandle = candle;
            }

            return candle;
        },

        removeCandle(candle, candleKey) {
            /*candle.base.remove(candle.tip);
                candle.root.remove(candle.base);
                candle.root.remove(candle.tip);
                this.scene.remove(candle.root);*/

            this.returnPoolObject('candles', candle);
            delete this._candles[candleKey];
        },

        evaluateBaseGraphTime(xTime) {
            return this.evaluateBaseGraph(xTime * this.graphPeriod - this.graphPeriod * 0.5);
        },

        evaluateBaseGraph(x) {
            //simple octaved sin
            var xProgress = (x / (this.graphPeriod / 2)) * 0.5 + 0.5;
            var xScaled = x * 2 + this.graphPeriod * 0.5;
            var sinBase = Math.sin(x) + 0.5 * Math.sin(x * 2) + 0.25 * Math.sin(x * 4) + 0.125 * Math.sin(x * 8);
            var sinOffset = Math.sin(xScaled) + 0.5 * Math.sin(xScaled * 2) + 0.25 * Math.sin(xScaled * 4) + 0.125 * Math.sin(xScaled * 8);
            return sinBase * 0.25 + sinOffset * 0.25 + this.noiseHelper.perlin2(xProgress * 10, 0) * 0.5;
        },

        getFreePoolObject(poolKey, objectConstructor) {
            var obj = null;
            if (this._objPools.free[poolKey]) {
                //console.log("get from pool");
                obj = this._objPools.free[poolKey].shift();
            } else {
                //console.log("get from pool - new");
                obj = objectConstructor();
            }

            if (isProxy(obj)) obj = toRaw(obj);

            if (obj) {
                if (!this._objPools.used[poolKey]) this._objPools.used[poolKey] = [];

                this._objPools.used[poolKey].push(obj);
            }

            return obj;
        },
        returnPoolObject(poolKey, object) {
            //console.log("return to pool");
            const index = (this._objPools.used[poolKey] || []).indexOf(object);
            //console.log(index);
            if (index !== -1) {
                delete this._objPools.used[poolKey][index];

                if (!this._objPools.free[poolKey]) this._objPools.free[poolKey] = [];

                this._objPools.free[poolKey].push(object);
            }
        },
        getPoolCount(poolKey) {
            return (this._objPools.free[poolKey] || []).length + (this._objPools.used[poolKey] || []).length;
        },

        getLerpedPos(values, index, progress) {
            if (index >= values.length) return values[values.length - 1];

            var fromPos = index > 0 ? values[index - 1] : values[index];
            var toPos = values[index];

            return fromPos.clone().lerp(toPos, progress);
        },

        getLerpedFloat(values, index, progress) {
            if (index >= values.length) return values[values.length - 1];

            var fromPos = index > 0 ? values[index - 1] : values[index];
            var toPos = values[index];

            return THREE.MathUtils.lerp(fromPos, toPos, progress);
        },

        getScrollPos() {
            var scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
            var scrollBottom = scrollTop + window.innerHeight;

            var lastSegPos = 0;
            var index = 0;
            for (var key in this._stops) {
                if (this._stops[key] > scrollTop) break;

                lastSegPos = this._stops[key];
                index++;
            }

            return {
                index: index,
                progress: index > 0 ? (scrollTop - this._stops[index - 1]) / (this._stops[index] - this._stops[index - 1]) : 100
            };
        },

        regenerateStops() {
            this._stops = [0];
            var anchors = document.getElementsByClassName('hkcm-graph-stop');
            for (var i = 0; i < anchors.length; i++) {
                this._stops.push(anchors[i].offsetTop);
            }
            var body = document.body,
                html = document.documentElement;

            this._stops.push(Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight));
        },

        _onResize(event) {
            this.camera.aspect = this.canvas.clientWidth / this.canvas.clientHeight;

            this._fov = 2 * Math.atan(this._cubeSize / 2 / (this._refDistance * (this.canvas.clientHeight / this._refCanvasHeight))) * (180 / Math.PI);

            this.camera.fov = (this._fov * this.canvas.clientHeight) / this._refCanvasHeight;
            this.camera.updateProjectionMatrix();

            //this.camera.position.z = this.camDistance * (this.canvas.clientHeight / this._refCanvasHeight);

            this.renderer.setSize(this.canvas.clientWidth, this.canvas.clientHeight);

            this.regenerateStops();
        },
        checkViewport() {
            const hkcmGraphStop1 = document.querySelector('#graph-background-zoom-stop-1');
            const hkcmGraphStop2 = document.querySelector('#graph-background-zoom-stop-2');
            let zoomingIn = this.isInViewport(hkcmGraphStop1);
            let fullyBlurred = this.isInViewport(hkcmGraphStop2);

            if (zoomingIn && fullyBlurred) {
                this.currentFps = this.fpsLimitFocus;
            } else if (!zoomingIn && fullyBlurred) {
                this.currentFps = this.fpsLimitBackground1;
            } else if (!zoomingIn && !fullyBlurred) {
                this.currentFps = this.fpsLimitBackground2;
            }

            this._fpsLimitInv = 1.0 / this.currentFps;
        },
        isInViewport(element) {
            const rect = element.getBoundingClientRect();
            return rect.bottom >= 0;
        }
    }
};
</script>
